// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Image, Linking, Pressable, ScrollView, StyleSheet } from 'react-native';
import { Redirect, Stack, useLocalSearchParams, useRouter } from 'expo-router';

import { Text, View } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { ClubLocatorMap } from '@/features/club/ClubLocatorMap';
import { buildMediaUrl } from '@/services/mediaUrl';
import appColors from '@/theme/colors';
import { layoutOpeningHours } from '@/utils/openingHours';

/** How the club's page renders its logo, in points. Requested at 2x for a crisp result. */
const LOGO_SIZE = 72;

type ClubCourse = {
  id: string;
  name: string;
  par: number;
  holesCount: number;
  teeCount: number;
};

type ClubFacility = {
  kind: string;
  name: string;
  openingHours: string | null;
  phone: string | null;
  websiteUrl: string | null;
};

type ClubDetail = {
  id: string;
  name: string;
  city: string;
  country: string;
  lat: number;
  lon: number;
  courses: ClubCourse[];
  websiteUrl: string | null;
  /** The key under images/, for buildMediaUrl. Never a bucket URL. */
  logoKey: string | null;
  description: string | null;
  /** The language the description is written in — often not the language of the app around it. */
  descriptionLang: string | null;
  /** The club's hours in the source's own words. Never parsed — see the render for why. */
  openingHours: string | null;
  /** Which source stated them. 'osm' renders plainly; anything else is qualified. */
  openingHoursSource: string | null;
  phone: string | null;
  facilities: ClubFacility[];
};

/**
 * Hours we will show without a qualifier.
 *
 * OpenStreetMap's `opening_hours` is a standardised syntax that editors maintain. Anything else —
 * a model's reading of a marketing page — is a guess, and a wrong opening time costs somebody a
 * drive, so it renders with a qualifier and the club's website beside it. An unknown source is
 * treated as the weakest, not the strongest: the whole point of ranking sources is lost if the
 * one case nobody anticipated renders as verified.
 */
const VERIFIED_HOURS_SOURCE = 'osm';

type LoadState =
  | { status: 'loading' }
  | { status: 'loaded'; club: ClubDetail }
  | { status: 'notFound' }
  | { status: 'error' };

/**
 * A golf club's own page: who the club is, and what it has to play.
 *
 * Everything the club presents about itself — logo, description, website — is optional and stays
 * absent rather than gaining a placeholder. A club nobody has enriched yet shows a name, a city and
 * its courses, which is the honest answer; a stock crest and a generated blurb would read as the
 * club's own. That is CLAUDE.md §19's rule about hole lengths, and it applies here word for word.
 */
export default function ClubScreen() {
  const { t } = useI18n();
  const router = useRouter();
  const { user } = useUser();
  const { clubId } = useLocalSearchParams<{ clubId?: string }>();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const load = useCallback(async () => {
    if (!clubId || !user?.token) {
      return;
    }

    setState({ status: 'loading' });
    try {
      const response = await fetch(`${apiBaseUrl}/api/geodata/clubs/${clubId}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      if (response.status === 404) {
        setState({ status: 'notFound' });
        return;
      }

      if (!response.ok) {
        setState({ status: 'error' });
        return;
      }

      const payload = (await response.json()) as ClubDetail;
      setState({
        status: 'loaded',
        club: {
          ...payload,
          courses: payload.courses ?? [],
          // Empty and absent both mean "nothing to show" — normalised to null so the render has
          // one falsy value to test rather than two.
          websiteUrl: payload.websiteUrl?.trim() || null,
          logoKey: payload.logoKey?.trim() || null,
          description: payload.description?.trim() || null,
          openingHours: payload.openingHours?.trim() || null,
          phone: payload.phone?.trim() || null,
          facilities: payload.facilities ?? [],
        },
      });
    } catch {
      setState({ status: 'error' });
    }
  }, [apiBaseUrl, clubId, user?.token]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!user) {
    return <Redirect href="/" />;
  }

  const club = state.status === 'loaded' ? state.club : null;

  return (
    <>
      <Stack.Screen options={{ title: club?.name ?? t('clubPage.title') }} />
      <ScrollView style={styles.page} contentContainerStyle={styles.content}>
        {state.status === 'loading' ? (
          <ActivityIndicator testID="club-page-loading" color={appColors.accent.primary} />
        ) : null}

        {state.status === 'notFound' ? (
          <Text testID="club-page-not-found" style={styles.message}>
            {t('clubPage.notFound')}
          </Text>
        ) : null}

        {state.status === 'error' ? (
          <>
            <Text testID="club-page-error" style={styles.message}>
              {t('clubPage.error')}
            </Text>
            <Pressable style={styles.retryButton} accessibilityRole="button" onPress={() => void load()}>
              <Text style={styles.retryText}>{t('clubPage.retry')}</Text>
            </Pressable>
          </>
        ) : null}

        {club ? (
          <>
            <View style={styles.header}>
              {/* No logo means no image, not a placeholder crest. */}
              {club.logoKey ? (
                <Image
                  testID="club-page-logo"
                  style={styles.logo}
                  resizeMode="contain"
                  accessibilityLabel={t('clubPage.logoAlt', { club: club.name })}
                  source={{
                    uri: buildMediaUrl('images', club.logoKey, 'icon', {
                      width: LOGO_SIZE * 2,
                      height: LOGO_SIZE * 2,
                    }),
                  }}
                />
              ) : null}
              <View style={styles.headerText}>
                <Text testID="club-page-name" style={styles.clubName}>
                  {club.name}
                </Text>
                {club.city || club.country ? (
                  <Text testID="club-page-place" style={styles.place}>
                    {[club.city, club.country].filter(Boolean).join(' · ')}
                  </Text>
                ) : null}
              </View>
            </View>

            {/* Rendered as-is, in the club's own language rather than the app's. `descriptionLang`
                says which that is; it is deliberately NOT put on the element as `lang` yet, because
                whether react-native-web forwards that attribute to the DOM is unverified here and a
                screen-reader hint that silently does nothing is worse than none. The field is
                carried so the admin review screen can label the prose, and so this can be wired up
                once somebody has checked it on web. */}
            {club.description ? (
              <Text testID="club-page-description" style={styles.description}>
                {club.description}
              </Text>
            ) : null}

            {/* Verbatim, never parsed. OpenStreetMap's opening_hours is a real grammar, and
                half-reading it — taking "Mo-Fr 08:00-20:00; Su off" as open on Sunday — would put
                a wrong answer in front of somebody deciding whether to drive an hour. Laying it
                out in lines is the one liberty taken: a venue per line, its name set apart, and
                the words themselves untouched. */}
            {club.openingHours ? (
              <View style={styles.hoursBlock}>
                <View testID="club-page-hours" style={styles.hoursLines}>
                  {((lines) =>
                    lines.map((line, index) => (
                      <Text
                        key={index}
                        testID="club-page-hours-line"
                        style={[
                          styles.hours,
                          // A line under a venue's name, without one of its own, sits inside it.
                          !line.label && lines.some((l) => l.label) ? styles.hoursContinuation : null,
                        ]}
                      >
                        {line.label ? (
                          <Text style={styles.hoursLabel}>{`${line.label}: `}</Text>
                        ) : null}
                        <Text>{line.text}</Text>
                      </Text>
                    )))(layoutOpeningHours(club.openingHours))}
                </View>
                {club.openingHoursSource !== VERIFIED_HOURS_SOURCE ? (
                  <Text testID="club-page-hours-unverified" style={styles.hoursQualifier}>
                    {t('clubPage.hoursFromWebsite')}
                  </Text>
                ) : null}
              </View>
            ) : null}

            {club.websiteUrl ? (
              <Pressable
                testID="club-page-website"
                accessibilityRole="link"
                onPress={() => void Linking.openURL(club.websiteUrl as string)}
              >
                <Text style={styles.website} numberOfLines={1}>
                  {club.websiteUrl}
                </Text>
              </Pressable>
            ) : null}

            {club.facilities.length > 0 ? (
              <>
                <Text style={styles.sectionTitle}>{t('clubPage.facilitiesTitle')}</Text>
                {club.facilities.map((facility) => (
                  <View
                    key={`${facility.kind}-${facility.name}`}
                    testID={`club-page-facility-${facility.kind}`}
                    style={styles.facilityRow}
                  >
                    <Text style={styles.facilityName}>
                      {`${t(`clubPage.facilityKind.${facility.kind}`)} · ${facility.name}`}
                    </Text>
                    {/* Its own hours and its own number, which is the point of listing facilities
                        one by one: the restaurant's lunch service is not the club's opening hours,
                        and its phone is not the club's switchboard. */}
                    {facility.openingHours ? (
                      <Text style={styles.facilityMeta}>{facility.openingHours}</Text>
                    ) : null}
                    {facility.phone ? (
                      <Pressable
                        testID={`club-page-facility-phone-${facility.kind}`}
                        accessibilityRole="link"
                        onPress={() => void Linking.openURL(`tel:${facility.phone as string}`)}
                      >
                        <Text style={styles.facilityLink}>{facility.phone}</Text>
                      </Pressable>
                    ) : null}
                  </View>
                ))}
              </>
            ) : null}

            {/* Null island is what a club with no known position holds, and it is a real place in
                the Atlantic — framing a map on it would state a location we do not have. */}
            {club.lat !== 0 || club.lon !== 0 ? (
              <>
                <Text style={styles.sectionTitle}>{t('clubPage.mapTitle')}</Text>
                <ClubLocatorMap center={{ lat: club.lat, lon: club.lon }} />
              </>
            ) : null}

            <Text style={styles.sectionTitle}>{t('clubPage.coursesTitle')}</Text>

            {club.courses.length === 0 ? (
              // A club is not hidden for having no courses a caller may see — it is a real club
              // with a name and possibly a website, and saying so beats an empty page.
              <Text testID="club-page-no-courses" style={styles.message}>
                {t('clubPage.noCourses')}
              </Text>
            ) : (
              club.courses.map((course) => (
                <Pressable
                  key={course.id}
                  testID={`club-page-course-${course.id}`}
                  accessibilityRole="button"
                  style={styles.courseRow}
                  onPress={() => router.push(`/course-details?courseId=${course.id}`)}
                >
                  <Text style={styles.courseName}>{course.name}</Text>
                  <Text style={styles.courseMeta}>
                    {t('clubPage.courseMeta', {
                      par: String(course.par),
                      holes: String(course.holesCount),
                      tees: String(course.teeCount),
                    })}
                  </Text>
                </Pressable>
              ))
            )}
          </>
        ) : null}
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  content: {
    padding: 16,
    gap: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerText: {
    flex: 1,
    gap: 2,
  },
  logo: {
    width: LOGO_SIZE,
    height: LOGO_SIZE,
    borderRadius: 8,
    backgroundColor: appColors.surface.card,
  },
  clubName: {
    fontSize: 22,
    fontWeight: '700',
    color: appColors.text.heading,
  },
  place: {
    fontSize: 14,
    color: appColors.text.secondary,
  },
  description: {
    fontSize: 15,
    lineHeight: 22,
    color: appColors.text.primary,
  },
  website: {
    fontSize: 14,
    color: appColors.accent.primary,
    textDecorationLine: 'underline',
  },
  hoursBlock: {
    gap: 2,
  },
  hoursLines: {
    gap: 2,
  },
  hours: {
    fontSize: 14,
    lineHeight: 20,
    color: appColors.text.primary,
  },
  hoursLabel: {
    fontWeight: '600',
  },
  hoursContinuation: {
    paddingLeft: 12,
  },
  hoursQualifier: {
    fontSize: 12,
    color: appColors.text.muted,
  },
  facilityRow: {
    paddingVertical: 6,
    gap: 2,
  },
  facilityName: {
    fontSize: 15,
    color: appColors.text.primary,
  },
  facilityMeta: {
    fontSize: 13,
    color: appColors.text.secondary,
  },
  facilityLink: {
    fontSize: 13,
    color: appColors.accent.primary,
    textDecorationLine: 'underline',
  },
  sectionTitle: {
    marginTop: 8,
    fontSize: 16,
    fontWeight: '600',
    color: appColors.text.heading,
  },
  courseRow: {
    padding: 12,
    borderRadius: 10,
    gap: 4,
    backgroundColor: appColors.surface.card,
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  courseName: {
    fontSize: 16,
    fontWeight: '600',
    color: appColors.text.primary,
  },
  courseMeta: {
    fontSize: 13,
    color: appColors.text.secondary,
  },
  message: {
    fontSize: 14,
    color: appColors.text.secondary,
  },
  retryButton: {
    alignSelf: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: appColors.surface.muted,
  },
  retryText: {
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.primary,
  },
});
